/**
 * OAuthCallback — Pagina intermedia del popup OAuth.
 * Ruta: /v2/oauth/callback
 * 
 * Recibe los query params que el backend redirige:
 *   ?meta_oauth=success&identity_id=...
 *   ?meta_oauth=error&reason=...
 * 
 * Hace postMessage al window.opener (ventana padre) y se cierra.
 * Si no hay opener (alguien abrio la URL directo), muestra resultado y link a brands.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function OAuthCallback() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState('processing'); // processing | success | error
  const [message, setMessage] = useState('');

  useEffect(() => {
    // Detectar tipo de respuesta
    const metaSuccess = params.get('meta_oauth') === 'success';
    const metaError = params.get('meta_oauth') === 'error';
    const identityId = params.get('identity_id');
    const reason = params.get('reason') || 'Error desconocido';

    let payload = null;

    if (metaSuccess && identityId) {
      payload = {
        type: 'OAUTH_RESULT',
        provider: 'meta',
        status: 'success',
        identity_id: identityId,
      };
      setStatus('success');
      setMessage('Conexion exitosa con Meta. Ya puedes cerrar esta ventana.');
    } else if (metaError) {
      payload = {
        type: 'OAUTH_RESULT',
        provider: 'meta',
        status: 'error',
        reason,
      };
      setStatus('error');
      setMessage(`Error en OAuth Meta: ${reason}`);
    } else {
      setStatus('error');
      setMessage('Parametros de callback invalidos.');
      return;
    }

    // Notificar al window padre y cerrar el popup
    if (window.opener && !window.opener.closed) {
      try {
        window.opener.postMessage(payload, window.location.origin);
      } catch (e) {
        console.error('postMessage failed:', e);
      }
      // Cerrar el popup despues de un breve delay para que el padre reciba
      setTimeout(() => {
        try { window.close(); } catch (e) {}
      }, 800);
    }
  }, [params]);

  const StatusIcon = status === 'success' ? CheckCircle2 : status === 'error' ? XCircle : Loader2;
  const iconColor = status === 'success' ? 'text-green-500' : status === 'error' ? 'text-red-500' : 'text-blue-500';

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-6">
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 max-w-md w-full text-center">
        <StatusIcon className={`h-16 w-16 mx-auto mb-4 ${iconColor} ${status === 'processing' ? 'animate-spin' : ''}`} />
        <h1 className="text-xl font-bold text-white mb-2">
          {status === 'success' && 'Listo'}
          {status === 'error' && 'Hubo un error'}
          {status === 'processing' && 'Procesando...'}
        </h1>
        <p className="text-sm text-slate-400 mb-6">{message}</p>
        
        {/* Fallback: si la ventana no se cierra sola */}
        {status !== 'processing' && (
          <div className="space-y-2">
            <Button
              onClick={() => window.close()}
              variant="outline"
              className="w-full"
            >
              Cerrar ventana
            </Button>
            {!window.opener && (
              <Link to="/v2/brands">
                <Button variant="ghost" className="w-full">
                  Volver a marcas
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
